import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Quote, Star } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialCard = ({ testimonial }) => (
  <div className="bg-zinc-900 p-8 rounded-2xl ring-1 ring-zinc-800 shadow-sm hover:ring-zinc-700 hover:-translate-y-1 transition-all duration-300 relative">
    <div className="absolute -top-4 left-8">
      <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
        <Quote size={16} className="text-white" />
      </div>
    </div>
    <div className="flex gap-1 mb-4 mt-2">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={16}
          className={i < (testimonial.rating || 5) ? 'fill-red-500 text-red-500' : 'text-zinc-700'}
        />
      ))}
    </div>
    <p className="text-gray-300 leading-relaxed mb-6">"{testimonial.text}"</p>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
        <span className="text-gray-300 font-semibold">{testimonial.name.charAt(0)}</span>
      </div>
      <div>
        <p className="font-semibold text-white">{testimonial.name}</p>
        <p className="text-sm text-gray-400">Verified Customer</p>
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  const { settings } = useSiteSettings();
  const [testimonials, setTestimonials] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/testimonials`)
      .then((res) => { if (active) setTestimonials(res.data || []); })
      .catch((err) => { console.error('Error fetching testimonials:', err); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  // Don't render fabricated placeholders — hide the section until real reviews exist.
  if (!loaded || testimonials.length === 0) return null;

  return (
    <section className="py-20 lg:py-24 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-red-600"></span>
            <span className="text-red-500 font-semibold text-xs uppercase tracking-[0.22em]">
              {settings.testimonialTagline || "Testimonials"}
            </span>
            <span className="h-px w-8 bg-red-600"></span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white tracking-tight mb-4">
            {settings.testimonialTitle || "A Hassle-Free Kind of Happy"}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {settings.testimonialSubtitle || "What Our Customers Are Saying"}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
